import React, { useEffect, useContext, useMemo, useState, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Flex } from 'rebass';
import { string, func, bool, array } from 'prop-types';
import join from 'url-join';
import { CSVLink } from 'react-csv';
import { ButtonStyled, TextStyled } from '@nike/nike-design-system-components';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import http from '~APP_UTIL/http';
import ActionItemList from '../../common/ActionItemList';
import AddDepartmentModal from './AddDepartmentModal';
import RemoveDepartmentModal from './RemoveDepartmentModal';

import TableServerSide from '../../../components/tableServerSide/TableServerSide';

import { getAccountContractColumns } from './accountContractColumns';

import { selectAdminAccount, fetchSelectedAccountDepartments } from '../actions';

import { getSelectedAccountId, getSelectedAccountDepartments, getIsSubmittingContractDeptChange } from '../selectors';
import { getAdminHeaderOpenState } from '../../selectors';

import { flatten } from '../../../util/array/flatten';
import { formatAccountContractForCSV, handleErrorNoAction } from '../../util';

import useModal from '../../../hooks/useModal';

const api2 = join(import.meta.env.VITE_API2_URL, 'v1');

const AccountContracts = ({
  selectAdminAccount,
  selectedAccountId,
  isAdminHeaderOpen,
  fetchSelectedAccountDepartments,
  departments,
  isSubmittingContractDeptChange
}) => {
  const { id } = useParams();

  const [allContracts, setAllContracts] = useState([]);
  const [selectedContractDepartmentData, setSelectedContractDepartmentData] = useState({
    contractId: null,
    contractNumber: null,
    departmentName: null,
    departmentNumber: null
  });

  const [isActionItemModalOpen, openActionItemModal, closeActionItemModal] = useModal();
  const [isAddDepartmentModalOpen, openAddDepartmentModal, closeAddDepartmentModal] = useModal();
  const [isRemoveDepartmentModalOpen, openRemoveDepartmentModal, closeRemoveDepartmentModal] = useModal();

  const csvLinkRef = useRef(null);

  const dispatch = useDispatch();

  useEffect(() => {
    // force account selection if user refreshes the page
    if (id && !selectedAccountId) {
      selectAdminAccount(id);
    }
  }, []);

  const {
    tableInstance: { selectedRowIds = {}, selectedFlatRows, isAllRowsSelected },
    totalResources,
    sortQueryParams,
    filterQueryParams,
    refreshTableData,
    handleClearSortAndFilters
  } = useContext(TableServerSideContext);

  useEffect(() => {
    refreshTableData();
    if (selectedAccountId) {
      fetchSelectedAccountDepartments(selectedAccountId);
    }
  }, [selectedAccountId, isAdminHeaderOpen]);

  useEffect(() => {
    if (!isSubmittingContractDeptChange) {
      if (isAddDepartmentModalOpen) {
        closeAddDepartmentModal();
        refreshTableData();
      } else if (isRemoveDepartmentModalOpen) {
        closeRemoveDepartmentModal();
        refreshTableData();
      }
    }
  }, [isSubmittingContractDeptChange]);

  const toggleAutoAssignedContract = async ({ toggle, contractId }) =>
    http
      .post(join(api2, `/contract/${contractId}/${toggle}AutoAssignedContract`), { accountId: id })
      .catch(error => handleErrorNoAction(dispatch, error));

  const handleOpenAddDepartmentModal = ({ contractNumber, contractId }) => {
    setSelectedContractDepartmentData({ contractNumber, contractId });
    openAddDepartmentModal();
  };

  const handleOpenRemoveDepartmentModal = ({ contractNumber, contractId, departmentNumber, departmentName }) => {
    setSelectedContractDepartmentData({ contractNumber, contractId, departmentNumber, departmentName });
    openRemoveDepartmentModal();
  };

  const memoizedColumns = useMemo(
    () =>
      getAccountContractColumns({
        toggleAutoAssignedContract,
        handleOpenAddDepartmentModal,
        handleOpenRemoveDepartmentModal
      }),
    []
  );

  // reformat response data so each contract group per contract is its own object/row
  const formatContractGroups = data => {
    let updatedContracts = [];
    data.forEach(({ contractGroups, ...contractData }) => {
      if (contractGroups.length > 0) {
        updatedContracts = [...updatedContracts, ...contractGroups.map(group => ({ ...contractData, group }))];
      } else {
        updatedContracts.push(contractData);
      }
    });
    return updatedContracts;
  };

  const clickCSVLink = async () => setTimeout(() => csvLinkRef?.current?.link.click(), 1000);

  const fetchAllContracts = async () => {
    const url = join(api2, `/contracts?filter=accountId(${id})&count=100${sortQueryParams}${filterQueryParams}`);
    const numberOfRequests = Math.ceil(totalResources / 100);

    const subsequentRequests = [];
    let anchor = 0;
    /* eslint-disable no-await-in-loop */
    for (let i = 0; i < numberOfRequests; i += 6) {
      const subsequestRequestsGroup = [];
      const limit = Math.min(6, numberOfRequests - i);
      for (let j = 0; j < limit; j++) {
        subsequestRequestsGroup.push(http.get(`${url}&anchor=${anchor}`));
        anchor += 100;
      }

      await Promise.all(subsequestRequestsGroup)
        .then(responses => responses.map(r => formatContractGroups(r.data.objects)))
        .then(result => subsequentRequests.push(flatten(result)));
    }
    /* eslint-disable no-await-in-loop */
    setAllContracts(flatten(subsequentRequests));
  };

  const csvLinkData = (isAllRowsSelected ? allContracts : selectedFlatRows?.map(row => row.original))?.map(
    ({ group, isEnabledAutoAssigned, id, ...contract }) =>
      formatAccountContractForCSV(contract, group, isEnabledAutoAssigned)
  );

  const actionItems = {
    Download: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                if (isAllRowsSelected) {
                  toggleIsLoading(true, 'Downloading CSV...');
                  await fetchAllContracts();
                  toggleIsLoading(false);
                }

                clickCSVLink();
                closeActionItemModal();
              }
          }
        ],
        content: () => 'Do you want to proceed with downloading the selected contract/s?',
        title: 'Confirm Download'
      }
    }
  };

  const departmentTypeaheadOptions = departments
    .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
    .map(department => `${department.number} ${department.name}`);

  return (
    <>
      <Box width='100%'>
        <Flex alignItems='center'>
          <TextStyled appearance='body2' style={{ marginLeft: '5px', marginRight: '10px' }}>
            {`${totalResources}`} results
          </TextStyled>

          <ButtonStyled
            appearance='text'
            background='light'
            ariaLabel='Clear All Filters'
            type='button'
            onClick={handleClearSortAndFilters}
            style={{ marginRight: '10px' }}
          >
            Clear All Filters
          </ButtonStyled>

          <ActionItemList
            selected={selectedRowIds}
            actionItems={actionItems}
            isModalOpen={isActionItemModalOpen}
            openModal={openActionItemModal}
            closeModal={closeActionItemModal}
          />
        </Flex>

        <CSVLink data={csvLinkData || []} filename='AccountContracts.csv' ref={csvLinkRef} target='_blank' />

        <TableServerSide
          columns={memoizedColumns}
          url={`v1/contracts?filter=accountId(${id})`}
          fullWidth
          fullHeight
          formatResponseObjects={formatContractGroups}
          includeRowSelect
          itemSize={100}
        />
      </Box>

      {isAddDepartmentModalOpen && (
        <AddDepartmentModal
          isOpen={isAddDepartmentModalOpen}
          closeModal={closeAddDepartmentModal}
          departmentTypeaheadOptions={departmentTypeaheadOptions}
          {...selectedContractDepartmentData}
        />
      )}
      {isRemoveDepartmentModalOpen && (
        <RemoveDepartmentModal
          isOpen={isRemoveDepartmentModalOpen}
          closeModal={closeRemoveDepartmentModal}
          {...selectedContractDepartmentData}
        />
      )}
    </>
  );
};

AccountContracts.propTypes = {
  selectedAccountId: string.isRequired,
  selectAdminAccount: func.isRequired,
  fetchSelectedAccountDepartments: func.isRequired,
  departments: array.isRequired,
  isAdminHeaderOpen: bool,
  isSubmittingContractDeptChange: bool.isRequired
};

AccountContracts.defaultProps = {
  isAdminHeaderOpen: false
};

const mapStateToProps = state => ({
  selectedAccountId: getSelectedAccountId(state),
  isAdminHeaderOpen: getAdminHeaderOpenState(state),
  departments: getSelectedAccountDepartments(state),
  isSubmittingContractDeptChange: getIsSubmittingContractDeptChange(state)
});

const mapDispatchToProps = { selectAdminAccount, fetchSelectedAccountDepartments };

export default connect(mapStateToProps, mapDispatchToProps)(AccountContracts);
